import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useGetCompanyFinanceChartDataMutation,
  useUpdateFinanceCurrencyMutation,
} from "../../../../store/Company/companyApiSlice";
import {
  CURRENCY_OPTIONS,
  METHOD_GRAPHS,
  UNIT_OPTIONS,
  canUseFinancials,
  hasReportingBasis,
  matchOptionValue,
  needsReportingBasis,
} from "./financialSetupFields";

/* The chooser's own state: which method is open, the reporting basis, and whether there is chart data to offer a fourth card for. */
export const useFinancialSetup = () => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [updateFinanceCurrency] = useUpdateFinanceCurrencyMutation();
  const [getCompanyFinanceChartData] = useGetCompanyFinanceChartDataMutation();

  /* Restored from the last visit, as the live chooser does. A malformed value
     must not take the section down with it. */
  const [stored] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("selectedObject")) || null;
    } catch {
      return null;
    }
  });

  const [openMethod, setOpenMethod] = useState(
    stored?.hideSetup ? stored?.selectedIndex ?? null : null
  );
  const [basis, setBasis] = useState({ currency: "", units: "" });
  const [chartData, setChartData] = useState(null);
  const [hasChartData, setHasChartData] = useState(false);
  const [basisError, setBasisError] = useState("");

  const allowed = canUseFinancials(currentEditingCompany);

  /* Transcribed: the currency selects are locked once the figures behind them have been verified — and ONLY then. */
  const basisLocked = Boolean(chartData?.is_currency_verified);

  /* Transcribed: the graphs card is offered only when at least one series has
     data. The live version checks eight of them by hand. */
  const loadChartData = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await getCompanyFinanceChartData({
        token,
        company_id: companyID,
        location: "dashboard",
      }).unwrap();

      const series = [
        res?.profitability?.revenue_to_earnings_graph?.data,
        res?.profitability?.roe_to_roa?.data,
        res?.liquidity?.quick_assets_to_liabilities?.data,
        res?.liquidity?.operating_cash_flow_ratio?.data,
        res?.liquidity?.free_cash_flow_ratio?.data,
        res?.efficiency?.cash_conversion_cycle?.data,
        res?.efficiency?.efficiency_ratios?.data,
        res?.financial,
      ];
      setHasChartData(series.some((s) => (s?.length ?? 0) > 0));
      setChartData(res);

      /* Seed the basis from the response. */
      const onFile = {
        currency: matchOptionValue(CURRENCY_OPTIONS, res?.currency),
        units: matchOptionValue(UNIT_OPTIONS, res?.units),
      };
      /* Only fills what is still empty: the request is async, so a viewer who
         picked a currency while it was in flight keeps their choice. */
      setBasis((prev) => ({
        currency: prev.currency || onFile.currency,
        units: prev.units || onFile.units,
      }));
    } catch {
      /* The live call is unwrapped and unguarded, so a failure became an
         unhandled rejection and the whole section went blank. Without chart
         data the fourth card is simply not offered. */
      setHasChartData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token]);

  useEffect(() => {
    loadChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID]);

  const setBasisField = (key, value) => {
    setBasis((prev) => ({ ...prev, [key]: value }));
    setBasisError("");
  };

  /* Opening a method is the live "Proceed": it records the choice and saves
   * the reporting basis. Every method but the graphs needs a basis first —
   * the figures are meaningless without knowing their currency and scale.
   */
  const openMethodPanel = async (methodId) => {
    if (!allowed) return;

    /* `basisLocked` exempts the gate as well as the selects. */
    if (
      needsReportingBasis(methodId) &&
      !basisLocked &&
      !hasReportingBasis(basis)
    ) {
      setBasisError("Choose a currency and an amount unit first.");
      return;
    }
    setBasisError("");
    setOpenMethod(methodId);

    try {
      localStorage.setItem(
        "selectedObject",
        JSON.stringify({ hideSetup: true, selectedIndex: methodId })
      );
    } catch {
      /* A full or blocked localStorage must not stop the panel opening; the
         only cost is that the flow will not resume on this method. */
    }

    if (methodId !== METHOD_GRAPHS && !basisLocked) {
      /* Transcribed call and payload. Not awaited by the live code either —
         but its rejection is caught here rather than escaping. */
      try {
        await updateFinanceCurrency({ data: basis, token, companyId: companyID });
      } catch {
        /* The basis is re-sent on the next commit; failing to store it must
           not block entering the figures. */
      }
    }
  };

  /* "Change method" — closes the panel and clears the resume marker, which is
     exactly what the live Back did. */
  const closeMethodPanel = () => {
    setOpenMethod(null);
    try {
      localStorage.removeItem("selectedObject");
    } catch {
      /* Nothing to recover from: the marker is a convenience. */
    }
  };

  return {
    allowed,
    companyName: currentEditingCompany?.registered_company_name || "This business",
    openMethod,
    openMethodPanel,
    closeMethodPanel,
    basis,
    setBasisField,
    basisLocked,
    basisError,
    hasChartData,
    chartData,
    refreshChartData: loadChartData,
  };
};

export default useFinancialSetup;
